/**
 * @file: useDeliveryCheckWizard.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useDeliveryCheckContext } from '@euler/app/flows/task/functions/task-detail/delivery-check/DeliveryCheckContext';
import { showAlert } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { useRef } from 'react';
import type { DeliveryCheckCellRef } from '../components/DeliveryCheckCell';

export const useDeliveryCheckWizard = () => {
  const { manager } = useDeliveryCheckContext();
  const [staged] = useBehaviorSubject(manager.staged$);
  const { currentIndex } = staged;
  const prevIndex = useRef<number>(currentIndex - 1);
  const wizardCellRef = useRef<DeliveryCheckCellRef>(null);

  const onPrev = usePersistFn(() => {
    if (currentIndex > 0) {
      prevIndex.current = currentIndex;
      manager.produce(draft => draft.currentIndex--);
    } else {
      wizardCellRef.current?.shake();
    }
  });

  const onNext = usePersistFn(() => {
    const item = staged.items[currentIndex];
    if (item != null) {
      if (item.selectedOptionIndex == null) {
        wizardCellRef.current?.shake(() => {
          void showAlert('请选择检查选项!');
        });
        return;
      }
      if (item.requiresPhoto && item.medias.length === 0) {
        wizardCellRef.current?.shake(() => {
          void showAlert('请拍照或者录制视频!');
        });
        return;
      }
    }
    if (currentIndex < staged.items.length - 1) {
      prevIndex.current = currentIndex;
      manager.produce(draft => draft.currentIndex++);
    } else {
      manager.produce(draft => (draft.mode = 'list'));
    }
  });

  const onSkip = usePersistFn(() => {
    if (currentIndex < staged.items.length - 1) {
      prevIndex.current = currentIndex;
      manager.produce(draft => draft.currentIndex++);
    }
  });

  const onShowAll = usePersistFn(() => {
    manager.produce(draft => (draft.mode = 'list'));
  });

  return {
    wizardCellRef,
    currentIndex,
    prevIndex,
    onPrev,
    onNext,
    onSkip,
    onShowAll,
  };
};
